// Functions (ISO 32000-1 §7.10) and colour spaces (§8.6).

import { Dict, PName, Stream, type PObj, type XRef, Lexer, PCmd } from "./parse";

// ---- functions ------------------------------------------------------------------------------------------------------------------------

export type PdfFunction = (inp: number[]) => number[];

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const nums = (o: PObj, xref: XRef): number[] => (Array.isArray(o) ? o.map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; }) : []);

export function makeFunction(o: PObj, xref: XRef): PdfFunction | null {
    const f = xref.resolve(o);
    if (Array.isArray(f)) {
        // an array of single-output functions
        const fs = f.map((x) => makeFunction(x, xref));
        if (fs.some((x) => !x)) return null;
        return (inp) => fs.map((fn) => fn!(inp)[0] ?? 0);
    }
    const d = f instanceof Stream ? f.dict : f instanceof Dict ? f : null;
    if (!d) return null;
    const type = d.num("FunctionType", -1);
    const domain = nums(d.get("Domain"), xref);
    const range = nums(d.get("Range"), xref);
    const clipOut = (out: number[]) => (range.length ? out.map((v, i) => clamp(v, range[2 * i] ?? -Infinity, range[2 * i + 1] ?? Infinity)) : out);
    const clipIn = (inp: number[]) => inp.map((v, i) => (domain.length ? clamp(v, domain[2 * i] ?? -Infinity, domain[2 * i + 1] ?? Infinity) : v));
    if (type === 2) {
        const c0 = nums(d.get("C0"), xref), c1 = nums(d.get("C1"), xref), n = d.num("N", 1);
        const a = c0.length ? c0 : [0], b = c1.length ? c1 : [1];
        return (inp) => { const x = clipIn(inp)[0] ?? 0; const t = n === 1 ? x : Math.pow(x, n); return clipOut(a.map((v, i) => v + t * ((b[i] ?? 1) - v))); };
    }
    if (type === 3) {
        const fns = (d.arr("Functions") ?? []).map((x) => makeFunction(x, xref));
        const bounds = nums(d.get("Bounds"), xref), encode = nums(d.get("Encode"), xref);
        if (fns.some((x) => !x)) return null;
        return (inp) => {
            const x = clipIn(inp)[0] ?? 0;
            const d0 = domain[0] ?? 0, d1 = domain[1] ?? 1;
            let k = 0;
            while (k < bounds.length && x >= bounds[k]!) k++;
            k = Math.min(k, fns.length - 1);
            const lo = k === 0 ? d0 : bounds[k - 1]!, hi = k === bounds.length ? d1 : bounds[k]!;
            const e0 = encode[2 * k] ?? 0, e1 = encode[2 * k + 1] ?? 1;
            const t = hi === lo ? e0 : e0 + ((x - lo) * (e1 - e0)) / (hi - lo);
            return clipOut(fns[k]!([t]));
        };
    }
    if (type === 0 && f instanceof Stream) {
        const size = nums(d.get("Size"), xref), bps = d.num("BitsPerSample", 8);
        const m = size.length, n = range.length / 2;
        const encode = nums(d.get("Encode"), xref), decode = nums(d.get("Decode"), xref);
        const data = f.bytes();
        const total = size.reduce((a, b) => a * b, 1) * n;
        const samples = new Float64Array(total);
        let bitPos = 0;
        const maxv = Math.pow(2, bps) - 1;
        for (let i = 0; i < total; i++) {
            let v = 0;
            for (let b = 0; b < bps; b += 8) { const take = Math.min(8, bps - b); v = v * Math.pow(2, take) + ((data[bitPos >> 3]! >> (8 - take - (bitPos & 7))) & ((1 << take) - 1)); bitPos += take; }
            samples[i] = v / maxv;
        }
        return (inp) => {
            const x = clipIn(inp);
            const idx: number[] = [], frac: number[] = [];
            for (let i = 0; i < m; i++) {
                const d0 = domain[2 * i] ?? 0, d1 = domain[2 * i + 1] ?? 1;
                const e0 = encode[2 * i] ?? 0, e1 = encode[2 * i + 1] ?? size[i]! - 1;
                let e = d1 === d0 ? e0 : e0 + ((x[i]! - d0) * (e1 - e0)) / (d1 - d0);
                e = clamp(e, 0, size[i]! - 1);
                idx.push(Math.floor(e)); frac.push(e - Math.floor(e));
            }
            const out: number[] = new Array(n).fill(0);
            const corners = 1 << m;
            for (let c = 0; c < corners; c++) {
                let w = 1, off = 0, stride = 1;
                for (let i = 0; i < m; i++) {
                    const hi = (c >> i) & 1;
                    w *= hi ? frac[i]! : 1 - frac[i]!;
                    off += Math.min(idx[i]! + hi, size[i]! - 1) * stride;
                    stride *= size[i]!;
                }
                if (w === 0) continue;
                for (let j = 0; j < n; j++) out[j]! += w * samples[off * n + j]!;
            }
            return clipOut(out.map((v, j) => { const d0 = decode[2 * j] ?? range[2 * j]!, d1 = decode[2 * j + 1] ?? range[2 * j + 1]!; return d0 + v * (d1 - d0); }));
        };
    }
    if (type === 4 && f instanceof Stream) {
        const prog = compilePs(f.bytes());
        if (!prog) return null;
        return (inp) => {
            const st = clipIn(inp).slice();
            runPs(prog, st);
            const n = range.length / 2;
            const out = st.slice(-n);
            while (out.length < n) out.unshift(0);
            return clipOut(out);
        };
    }
    return null;
}

type PsOp = number | string | PsOp[];

function compilePs(data: Uint8Array): PsOp[] | null {
    const lx = new Lexer(data);
    const stack: PsOp[][] = [];
    let cur: PsOp[] = [];
    let t;
    let started = false;
    while ((t = lx.next()) !== undefined) {
        if (t instanceof PCmd) {
            if (t.cmd === "{") { if (!started) { started = true; continue; } stack.push(cur); cur = []; continue; }
            if (t.cmd === "}") { if (!stack.length) return cur; const inner = cur; cur = stack.pop()!; cur.push(inner); continue; }
            cur.push(t.cmd);
        } else if (typeof t === "number") cur.push(t);
    }
    return cur;
}

function runPs(prog: PsOp[], st: number[]) {
    for (let i = 0; i < prog.length; i++) {
        const op = prog[i]!;
        if (typeof op === "number") { st.push(op); continue; }
        if (Array.isArray(op)) {
            const next = prog[i + 1];
            if (next === "if") { const c = st.pop(); if (c) runPs(op, st); i++; }
            else if (Array.isArray(next) && prog[i + 2] === "ifelse") { const c = st.pop(); runPs(c ? op : next, st); i += 2; }
            continue;
        }
        const pop = () => st.pop() ?? 0;
        switch (op) {
            case "add": { const b = pop(), a = pop(); st.push(a + b); break; }
            case "sub": { const b = pop(), a = pop(); st.push(a - b); break; }
            case "mul": { const b = pop(), a = pop(); st.push(a * b); break; }
            case "div": { const b = pop(), a = pop(); st.push(b ? a / b : 0); break; }
            case "idiv": { const b = pop(), a = pop(); st.push(b ? Math.trunc(a / b) : 0); break; }
            case "mod": { const b = pop(), a = pop(); st.push(b ? a % b : 0); break; }
            case "neg": st.push(-pop()); break;
            case "abs": st.push(Math.abs(pop())); break;
            case "ceiling": st.push(Math.ceil(pop())); break;
            case "floor": st.push(Math.floor(pop())); break;
            case "round": st.push(Math.round(pop())); break;
            case "truncate": st.push(Math.trunc(pop())); break;
            case "sqrt": st.push(Math.sqrt(pop())); break;
            case "sin": st.push(Math.sin((pop() * Math.PI) / 180)); break;
            case "cos": st.push(Math.cos((pop() * Math.PI) / 180)); break;
            case "atan": { const b = pop(), a = pop(); let r = (Math.atan2(a, b) * 180) / Math.PI; if (r < 0) r += 360; st.push(r); break; }
            case "exp": { const b = pop(), a = pop(); st.push(Math.pow(a, b)); break; }
            case "ln": st.push(Math.log(pop())); break;
            case "log": st.push(Math.log10(pop())); break;
            case "cvi": st.push(Math.trunc(pop())); break;
            case "cvr": break;
            case "eq": { const b = pop(), a = pop(); st.push(a === b ? 1 : 0); break; }
            case "ne": { const b = pop(), a = pop(); st.push(a !== b ? 1 : 0); break; }
            case "gt": { const b = pop(), a = pop(); st.push(a > b ? 1 : 0); break; }
            case "ge": { const b = pop(), a = pop(); st.push(a >= b ? 1 : 0); break; }
            case "lt": { const b = pop(), a = pop(); st.push(a < b ? 1 : 0); break; }
            case "le": { const b = pop(), a = pop(); st.push(a <= b ? 1 : 0); break; }
            case "and": { const b = pop(), a = pop(); st.push(a & b); break; }
            case "or": { const b = pop(), a = pop(); st.push(a | b); break; }
            case "xor": { const b = pop(), a = pop(); st.push(a ^ b); break; }
            case "not": { const a = pop(); st.push(a === 0 ? 1 : a === 1 ? 0 : ~a); break; }
            case "bitshift": { const s = pop(), a = pop(); st.push(s >= 0 ? a << s : a >> -s); break; }
            case "true": st.push(1); break;
            case "false": st.push(0); break;
            case "pop": pop(); break;
            case "exch": { const b = pop(), a = pop(); st.push(b, a); break; }
            case "dup": { const a = pop(); st.push(a, a); break; }
            case "copy": { const n = pop(); if (n > 0 && n <= st.length) st.push(...st.slice(-n)); break; }
            case "index": { const n = pop(); st.push(st[st.length - 1 - n] ?? 0); break; }
            case "roll": { const j = pop(), n = pop(); if (n > 0 && n <= st.length) { const part = st.splice(st.length - n, n); const s = ((j % n) + n) % n; for (let k = 0; k < n; k++) st.push(part[(k - s + n) % n]!); } break; }
        }
    }
}

// ---- colour spaces --------------------------------------------------------------------------------------------------------------------

export type RGB = [number, number, number];

export interface ColorSpace {
    name: string;
    /** Number of components. */
    n: number;
    /** Components (0–1, or the space's own range) → 0–255 RGB. */
    rgb(c: number[]): RGB;
    /** The default colour. */
    initial(): number[];
    /** Default Decode array for images with `bpc` bits. */
    decode(bpc: number): number[];
    /** For Pattern spaces: the underlying space. */
    base?: ColorSpace;
}

const to255 = (v: number) => Math.round(clamp(v, 0, 1) * 255);

const gray: ColorSpace = { name: "DeviceGray", n: 1, rgb: (c) => { const g = to255(c[0] ?? 0); return [g, g, g]; }, initial: () => [0], decode: () => [0, 1] };
const rgb: ColorSpace = { name: "DeviceRGB", n: 3, rgb: (c) => [to255(c[0] ?? 0), to255(c[1] ?? 0), to255(c[2] ?? 0)], initial: () => [0, 0, 0], decode: () => [0, 1, 0, 1, 0, 1] };
const cmyk: ColorSpace = {
    name: "DeviceCMYK", n: 4,
    rgb: (c) => {
        const C = clamp(c[0] ?? 0, 0, 1), M = clamp(c[1] ?? 0, 0, 1), Y = clamp(c[2] ?? 0, 0, 1), K = clamp(c[3] ?? 0, 0, 1);
        // a polynomial fit of the SWOP profile, as readers commonly use
        const r = 255 + C * (-4.387332384609988 * C + 54.48615194189176 * M + 18.82290502165302 * Y + 212.25662451639585 * K - 285.2331026137004) + M * (1.7149763477362134 * M - 5.6096736904047315 * Y - 17.873870861415444 * K - 5.497006427196366) + Y * (-2.5217340131683033 * Y - 21.248923337353073 * K + 17.5119270841813) + K * (-21.86122147463605 * K - 189.48180835922747);
        const g = 255 + C * (8.841041422036149 * C + 60.118027045597366 * M + 6.871425592049007 * Y + 31.159100130055922 * K - 79.2970844816548) + M * (-15.310361306967817 * M + 17.575251261109482 * Y + 131.35250912493976 * K - 190.9453302588951) + Y * (4.444339102852739 * Y + 9.8632861493405 * K - 24.86741582555878) + K * (-20.737325471181034 * K - 187.80453709719578);
        const b = 255 + C * (0.8842522430003296 * C + 8.078677503112928 * M + 30.89978309703729 * Y - 0.23883238689178934 * K - 14.183576799673286) + M * (10.49593273432072 * M + 63.02378494754052 * Y + 50.606957656360734 * K - 112.23884253719248) + Y * (0.03296041114873217 * Y + 115.60384449646641 * K - 193.58209356861505) + K * (-22.33816807309886 * K - 180.12613974708367);
        return [clamp(Math.round(r), 0, 255), clamp(Math.round(g), 0, 255), clamp(Math.round(b), 0, 255)];
    },
    initial: () => [0, 0, 0, 1], decode: () => [0, 1, 0, 1, 0, 1, 0, 1]
};

export function makeColorSpace(o: PObj, res: Dict | null, xref: XRef, depth = 0): ColorSpace {
    const v = xref.resolve(o);
    if (depth > 6) return gray;
    if (v instanceof PName) {
        switch (v.name) {
            case "DeviceGray": case "G": case "CalGray": return gray;
            case "DeviceRGB": case "RGB": case "CalRGB": return rgb;
            case "DeviceCMYK": case "CMYK": return cmyk;
            case "Pattern": return { name: "Pattern", n: 0, rgb: () => [0, 0, 0], initial: () => [], decode: () => [] };
            default: {
                const cs = res?.dict("ColorSpace")?.get(v.name);
                if (cs && !(cs instanceof PName && cs.name === v.name)) return makeColorSpace(cs, res, xref, depth + 1);
                return gray;
            }
        }
    }
    if (!Array.isArray(v) || !v.length) return gray;
    const kind = xref.resolve(v[0]);
    const k = kind instanceof PName ? kind.name : "";
    switch (k) {
        case "DeviceGray": case "G": case "CalGray": return gray;
        case "DeviceRGB": case "RGB": case "CalRGB": return rgb;
        case "DeviceCMYK": case "CMYK": return cmyk;
        case "Lab": {
            const d = xref.resolve(v[1]);
            const wp = d instanceof Dict ? nums(d.get("WhitePoint"), xref) : [0.9505, 1, 1.089];
            const rg = d instanceof Dict ? nums(d.get("Range"), xref) : [];
            const [amin, amax, bmin, bmax] = [rg[0] ?? -100, rg[1] ?? 100, rg[2] ?? -100, rg[3] ?? 100];
            return {
                name: "Lab", n: 3, initial: () => [0, 0, 0], decode: () => [0, 100, amin, amax, bmin, bmax],
                rgb: (c) => {
                    const L = c[0] ?? 0, a = clamp(c[1] ?? 0, amin, amax), b = clamp(c[2] ?? 0, bmin, bmax);
                    const M = (L + 16) / 116, Lx = M + a / 500, Nz = M - b / 200;
                    const g = (x: number) => (x >= 6 / 29 ? x * x * x : (108 / 841) * (x - 4 / 29));
                    const X = (wp[0] ?? 0.9505) * g(Lx), Y = (wp[1] ?? 1) * g(M), Z = (wp[2] ?? 1.089) * g(Nz);
                    const lin = [3.2406 * X - 1.5372 * Y - 0.4986 * Z, -0.9689 * X + 1.8758 * Y + 0.0415 * Z, 0.0557 * X - 0.204 * Y + 1.057 * Z];
                    const gm = (u: number) => (u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055);
                    return [to255(gm(lin[0]!)), to255(gm(lin[1]!)), to255(gm(lin[2]!))];
                }
            };
        }
        case "ICCBased": {
            const s = xref.resolve(v[1]);
            const n = s instanceof Stream ? s.dict.num("N", 3) : 3;
            const alt = s instanceof Stream ? s.dict.get("Alternate") : null;
            if (alt) { const a = makeColorSpace(alt, res, xref, depth + 1); if (a.n === n) return a; }
            return n === 1 ? gray : n === 4 ? cmyk : rgb;
        }
        case "Indexed": case "I": {
            const base = makeColorSpace(v[1]!, res, xref, depth + 1);
            const hival = xref.resolve(v[2]) as number;
            const lk = xref.resolve(v[3]);
            let table: Uint8Array;
            if (lk instanceof Stream) table = lk.bytes();
            else if (typeof lk === "string") { table = new Uint8Array(lk.length); for (let i = 0; i < lk.length; i++) table[i] = lk.charCodeAt(i); }
            else table = new Uint8Array(0);
            const cache = new Map<number, RGB>();
            return {
                name: "Indexed", n: 1, base, initial: () => [0], decode: (bpc) => [0, Math.pow(2, bpc) - 1],
                rgb: (c) => {
                    const i = clamp(Math.round(c[0] ?? 0), 0, hival);
                    const hit = cache.get(i);
                    if (hit) return hit;
                    const comps: number[] = [];
                    const dec = base.decode(8);
                    for (let j = 0; j < base.n; j++) { const raw = (table[i * base.n + j] ?? 0) / 255; const lo = dec[2 * j] ?? 0, hi = dec[2 * j + 1] ?? 1; comps.push(lo + raw * (hi - lo)); }
                    const r = base.rgb(comps);
                    cache.set(i, r);
                    return r;
                }
            };
        }
        case "Separation": case "DeviceN": {
            const names = k === "Separation" ? [v[1]] : (xref.resolve(v[1]) as PObj[]);
            const n = Array.isArray(names) ? names.length : 1;
            const alt = makeColorSpace(v[2]!, res, xref, depth + 1);
            const fn = makeFunction(v[3]!, xref);
            const isNone = k === "Separation" && xref.resolve(v[1]) instanceof PName && (xref.resolve(v[1]) as PName).name === "None";
            return {
                name: k, n, initial: () => new Array(n).fill(1), decode: () => new Array(n * 2).fill(0).map((_, i) => i & 1),
                rgb: (c) => { if (isNone) return [255, 255, 255]; const out = fn ? fn(c) : c; return alt.rgb(out); }
            };
        }
        case "Pattern": {
            const base = v[1] ? makeColorSpace(v[1], res, xref, depth + 1) : undefined;
            return { name: "Pattern", n: base?.n ?? 0, base, rgb: (c) => (base ? base.rgb(c) : [0, 0, 0]), initial: () => [], decode: () => [] };
        }
    }
    return gray;
}

export const DEVICE_GRAY = gray;
export const DEVICE_RGB = rgb;
export const DEVICE_CMYK = cmyk;
